import React, { FC, useEffect, useLayoutEffect, useRef } from 'react';
import { OpeningApplicantCard, Skeleton, Placeholder } from '@src/containers/Feed/OpeningApplicantCard';
import { useAppDispatch } from '@src/redux';
import { setModalContent } from '@src/redux/actions/modal';
import { useQueryParams } from '@src/customHooks/useQueryParams';
import { isVerifiedUserApplicantType } from '@src/containers/Feed/OpeningApplicantCard/typeGuards/isVerifiedUserApplicantType';
import { isOpeningApplicantType } from '@src/containers/Feed/OpeningApplicantCard/typeGuards/isOpeningApplicantType';
import { InviteToInterview } from '@src/components/VerifyTalentCard/InviteToInterview';
import {
  ApplicantsSortOptionEnum,
  ApplicationSourceEnum,
  MessageThreadStateEnum,
  RecruiterApplicationStateEnum,
  UserApplicationStateEnum,
  useAcceptApplicationMutation,
  useRejectApplicationMutation,
} from '@src/graphql/generated';
import { ApplicantCardActionTypes } from '@src/containers/Feed/OpeningApplicantCard/enums/applicantCardActionTypes.enum';
import { setErrorFlashMessage, setFlashMessage, setSuccessFlashMessage } from '@src/redux/actions/flashMessages';
import { createLink } from '@src/util/createLink';
import { FUNNEL_ROUTE } from '@src/Routes/links';
import { trackEvent } from '@src/metrics';
import { MetricAction, MetricContext } from '@src/metrics/metrics.enum';
import { QueryParams } from '../interfaces/queryParams.interface';
import { ActiveView } from '../enums/activeView.enum';
import { useGetOpeningApplicants } from '../hooks/useGetOpeningApplicants';
import { onAcceptParams } from '../interfaces/onAccept.interface';
import { useGetOpeningId } from '../hooks/useGetOpeningId';
import { onRejectParams } from '../interfaces/onReject.interface';
import { InShortlistHeader } from './InShortlistHeader';
import { InShortlistProps } from './props';
import './styles.scss';

export const InShortlist: FC<InShortlistProps> = ({ totalShortlist }) => {
  const isMounted = useRef(false);

  const dispatch = useAppDispatch();

  const openingId = useGetOpeningId();

  const { queryParams, updateQueryParams, removeQueryParams } = useQueryParams<QueryParams>();

  const sortOption = queryParams.inShortlistUsersSortOption || ApplicantsSortOptionEnum.USER_APPLIED_AT;

  const {
    openingApplicants,
    openingApplicantsPagination,
    isOpeningApplicantsLoading,
    loadMoreOpeningApplicants,
    refetchOpeningApplicants,
  } = useGetOpeningApplicants({
    fetchPolicy: 'network-only',
    firstBatchSize: 6,
    nextBatchSize: 3,
    recruiterMessageThreadStates: [MessageThreadStateEnum.OPEN],
    recruiterStates: [RecruiterApplicationStateEnum.PENDING],
    sortBy: sortOption,
    sources: [ApplicationSourceEnum.OPENING, ApplicationSourceEnum.USER_VERIFICATION],
    userStates: [UserApplicationStateEnum.ACCEPTED],
  });

  const [acceptApplication] = useAcceptApplicationMutation({
    onCompleted: async data => {
      dispatch(
        setFlashMessage({
          linkLabel: 'Open chat',
          message: `${data.acceptApplication.user.name} has been moved to “In conversation” where you can discuss next steps directly.`,
          shouldDisableAutoHide: false,
          title: 'Candidate accepted',
          to: createLink(FUNNEL_ROUTE, {
            params: {
              openingId: openingId,
            },
            queryParams: {
              activeView: ActiveView.IN_CONVERSATION,
              applicationId: data.acceptApplication.id,
            },
          }),
          type: 'success',
        }),
      );

      await refetchOpeningApplicants();
    },
    onError: () => {
      dispatch(setErrorFlashMessage('Error accepting application'));
    },
  });

  const [rejectApplication] = useRejectApplicationMutation({
    onCompleted: async () => {
      dispatch(setSuccessFlashMessage('Application rejected'));

      await refetchOpeningApplicants();
    },
    onError: () => {
      dispatch(setErrorFlashMessage('Error rejecting application'));
    },
  });

  const onAccept = async ({ userMatchingProfileId, openingId }: onAcceptParams) => {
    await acceptApplication({
      variables: {
        openingId: openingId,
        userMatchingProfileId: userMatchingProfileId,
      },
    });

    trackEvent({
      context: MetricContext.OPENING_VIEW,
      interaction: MetricAction.ACCEPT_APPLICATION,
      payload: {
        'Event Source': queryParams?.activeView,
        'Opening Id': openingId,
        'User Matching Profile Id': userMatchingProfileId,
      },
    });
  };

  const onReject = async ({ userMatchingProfileId, openingId }: onRejectParams) => {
    await rejectApplication({
      variables: {
        openingId: openingId,
        userMatchingProfileId: userMatchingProfileId,
      },
    });

    trackEvent({
      context: MetricContext.OPENING_VIEW,
      interaction: MetricAction.REJECT_APPLICATION,
      payload: {
        'Event Source': queryParams?.activeView,
        'Opening Id': openingId,
        'User Matching Profile Id': userMatchingProfileId,
      },
    });
  };

  const openVerifiedTalent = (userId: string): void => {
    dispatch(
      setModalContent({
        className: 'opening-in-shortlist__modal-wrapper',
        config: {
          withoutPadding: true,
        },
        content: (
          <InviteToInterview
            areVerificationStagesHidden
            shouldFetchApplicant
            modalOptions={{
              shouldOnlyShowProfile: true,
            }}
            openingId={openingId}
            onClose={() => {
              removeQueryParams(['userId']);
            }}
            userId={userId}
          />
        ),
        onClose: () => {
          removeQueryParams(['userId']);
        },
      }),
    );
  };

  const openOpeningApplicant = (applicationId: string): void => {
    updateQueryParams({
      applicationId: applicationId,
    });
  };

  useLayoutEffect(() => {
    const checkScrollPositionAndLoadMore = () => {
      const {
        body: { scrollHeight },
      } = document;
      const { innerHeight, scrollY } = window;
      const distanceFromBottom = scrollHeight - innerHeight - scrollY;

      if (!isOpeningApplicantsLoading && openingApplicantsPagination?.hasMoreResults && distanceFromBottom < 100) {
        loadMoreOpeningApplicants();
      }
    };

    window.addEventListener('scroll', checkScrollPositionAndLoadMore);

    return () => {
      window.removeEventListener('scroll', checkScrollPositionAndLoadMore);

      loadMoreOpeningApplicants.cancel();
    };
  }, [isOpeningApplicantsLoading, openingApplicantsPagination?.hasMoreResults]);

  useEffect(() => {
    if (isMounted.current) {
      refetchOpeningApplicants();
    }

    isMounted.current = true;
  }, [totalShortlist]);

  useEffect(() => {
    if (queryParams.userId) {
      openVerifiedTalent(queryParams.userId);
    }
  }, [queryParams.userId]);

  return (
    <div className="opening-in-shortlist">
      <div className="opening-in-shortlist__header">
        <InShortlistHeader />
      </div>

      <div className="opening-in-shortlist__cards">
        {!openingApplicants?.length && !isOpeningApplicantsLoading && (
          <>
            <Placeholder
              message="No applicants yet"
              onClick={() =>
                updateQueryParams({
                  activeView: ActiveView.INVITE_CANDIDATES,
                })
              }
              wrapperClassName="opening-in-shortlist__card"
            />
            <Placeholder wrapperClassName="opening-in-shortlist__card" />
            <Placeholder wrapperClassName="opening-in-shortlist__card" />
          </>
        )}

        {openingApplicants?.map(applicant => {
          return (
            <div key={applicant.user.id}>
              <OpeningApplicantCard
                actionType={ApplicantCardActionTypes.PENDING}
                isUnread={!!applicant.application.messageThread?.isUnread}
                onAccept={() => {
                  const userMatchingProfileId = applicant.application.userMatchingProfile.id;

                  if (openingId && userMatchingProfileId) {
                    onAccept({
                      openingId: openingId,
                      userMatchingProfileId: userMatchingProfileId,
                    });
                  }
                }}
                onCardClick={() => {
                  updateQueryParams({ userId: applicant.user.id });
                }}
                onDecline={() => {
                  const userMatchingProfileId = applicant.application.userMatchingProfile.id;

                  if (openingId && userMatchingProfileId) {
                    onReject({
                      openingId: openingId,
                      userMatchingProfileId: userMatchingProfileId,
                    });
                  }
                }}
                onOpenChat={() => {
                  if (isVerifiedUserApplicantType(applicant) || isOpeningApplicantType(applicant)) {
                    openOpeningApplicant(applicant.application.id);
                  }
                }}
                userData={applicant}
                wrapperClassName="opening-in-shortlist__card"
              />
            </div>
          );
        })}

        {isOpeningApplicantsLoading && (
          <>
            <Skeleton />
            <Skeleton />
            <Skeleton />
          </>
        )}
      </div>
    </div>
  );
};
